import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { storeFormEntry } from "@/utils/formEntryStore";
import { submitFormToSupabase } from "@/utils/submitFormToSupabase";
import { uploadFileToCloud } from "@/utils/cloudImageUpload";

interface JobListing {
  id: string;
  title: string;
  type: string;
  description: string;
  requirements: string[];
  visible: boolean;
}

interface CareersData {
  pageVisible: boolean;
  heading: string;
  subheading: string;
  intro: string;
  comingSoonText: string;
  jobs: JobListing[];
  formHeading: string;
  formSubheading: string;
}

const DEFAULT_CAREERS: CareersData = {
  pageVisible: true,
  heading: "Join Our Team",
  subheading: "Careers at Makers Alley Co.",
  intro: "We're building something special in the Rogue Valley — a permanent home for independent makers. If you love art, community, and creating great retail experiences, we'd love to meet you.",
  comingSoonText: "We're not hiring right now, but we're always growing. Check back soon for open positions!",
  formHeading: "Apply Now",
  formSubheading: "Submit your application and resume below. We'll be in touch!",
  jobs: [
    {
      id: "sales-associate",
      title: "Sales Associate",
      type: "Part-Time",
      description: "Be the face of Makers Alley Co. You'll help customers discover handmade goods, assist artists with their spaces, and keep the shop running smoothly.",
      requirements: [
        "Friendly, outgoing personality",
        "Passion for handmade art and local makers",
        "Retail or customer service experience preferred",
        "Ability to work weekends",
        "Reliable and punctual",
      ],
      visible: true,
    },
    {
      id: "assistant-manager",
      title: "Assistant Store Manager",
      type: "Full-Time",
      description: "Support the Store Manager in day-to-day operations, vendor relations, and team leadership. Help us create an exceptional experience for both artists and shoppers.",
      requirements: [
        "1+ years retail management or supervisory experience",
        "Strong organizational and communication skills",
        "Experience with POS systems",
        "Ability to manage a small team",
        "Passion for supporting local artists",
      ],
      visible: true,
    },
    {
      id: "store-manager",
      title: "Store Manager",
      type: "Full-Time",
      description: "Lead the Makers Alley Co. team and oversee all store operations, vendor onboarding, scheduling, and community partnerships. This is a leadership role for someone who loves both retail and the arts.",
      requirements: [
        "3+ years retail management experience",
        "Proven leadership and team-building skills",
        "Experience with inventory, scheduling, and budgeting",
        "Strong community and vendor relations skills",
        "Passion for handmade goods and local business",
      ],
      visible: true,
    },
  ],
};

export default function CareersPage() {
  const { isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [data, setData] = useAdminStorage<CareersData>("ma_careers_v1", DEFAULT_CAREERS);

  // REMOVE redirect — visitors should see the Coming Soon page, not get sent home
  useEffect(() => {
    // No redirect: the coming soon page below is the intended visitor experience
    // when pageVisible is false. Admins can toggle visibility to show full content.
  }, [data.pageVisible, isAdmin, navigate]);

  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const set = useCallback(<K extends keyof CareersData>(key: K, val: CareersData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateJob = useCallback((id: string, field: keyof JobListing, val: string | boolean | string[]) => {
    const jobs = data.jobs.map((j) => j.id === id ? { ...j, [field]: val } : j);
    setData({ ...data, jobs });
  }, [data, setData]);

  const toggleJobVisible = useCallback((id: string) => {
    const jobs = data.jobs.map((j) => j.id === id ? { ...j, visible: !j.visible } : j);
    setData({ ...data, jobs });
  }, [data, setData]);

  const addRequirement = useCallback((id: string) => {
    const job = data.jobs.find((j) => j.id === id);
    if (!job) return;
    updateJob(id, "requirements", [...job.requirements, "New requirement"]);
  }, [data, updateJob]);

  const removeRequirement = useCallback((id: string, idx: number) => {
    const job = data.jobs.find((j) => j.id === id);
    if (!job) return;
    updateJob(id, "requirements", job.requirements.filter((_, i) => i !== idx));
  }, [data, updateJob]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resumeFile && !resumeUrl) {
      setResumeError("Please upload your resume before submitting.");
      return;
    }
    setResumeError(null);
    setStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const fullName = formData.get("fullName")?.toString() ?? "";
    const email = formData.get("email")?.toString() ?? "";
    const phone = formData.get("phone")?.toString() ?? "";
    const availability = formData.get("availability")?.toString() ?? "";
    const whyApply = formData.get("whyApply")?.toString() ?? "";
    const position = formData.get("position")?.toString() ?? "";

    // Upload resume if a new file was selected
    let finalResumeUrl = resumeUrl ?? "";
    if (resumeFile) {
      try {
        setResumeUploading(true);
        finalResumeUrl = await uploadFileToCloud(resumeFile);
      } catch (err) {
        console.error("[CareersPage] Resume upload failed:", err);
        // Fall back to just storing the filename
        finalResumeUrl = resumeFile.name;
      } finally {
        setResumeUploading(false);
      }
    }

    const careerFields = [
      { label: "Full Name", value: fullName },
      { label: "Email", value: email },
      { label: "Phone", value: phone },
      { label: "Availability", value: availability },
      { label: "Why Apply", value: whyApply },
      { label: "Position", value: position },
      { label: "Resume", value: finalResumeUrl || resumeFile?.name || "No file uploaded" },
    ];
    storeFormEntry("career", `Career Application - ${position}`, careerFields);
    submitFormToSupabase("career", `Career Application - ${position}`, careerFields).then((ok) => {
      if (!ok) console.error("[CareersPage] Form submission to Supabase failed");
    });

    setStatus("success");
    form.reset();
    setResumeFile(null);
    setResumeUrl(null);
    setSelectedJob(null);
  };

  const bg = isDark ? "bg-gray-950" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const inputCls = isDark
    ? "w-full border border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300";

  // Only show visible jobs to public; admin sees all with toggle controls
  const displayedJobs = isAdmin ? data.jobs : data.jobs.filter((j) => j.visible);

  // Coming soon page (when pageVisible is false)
  if (!data.pageVisible) {
    return (
      <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
        <SEOHead
          title="Careers at Makers Alley Co — Join Our Team in Medford, Oregon"
          description="Join the Makers Alley Co team in Medford, Oregon. We're hiring Sales Associates, Assistant Store Managers, and Store Managers who love handmade art and local community."
          keywords="Makers Alley jobs, retail jobs Medford Oregon, art shop careers, sales associate Medford, store manager Oregon, handmade retail jobs"
          canonicalPath="/careers"
          ogType="website"
          ogTitle="Careers at Makers Alley Co — Join Our Team"
          ogDescription="We're hiring in Medford, Oregon. Sales Associates, Assistant Store Managers, and Store Managers who love handmade art and local community."
          lastModified="2026-05-10"
          author="Makers Alley Co"
        />
        <PreviewModeBar />
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 py-32 mt-14">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 flex items-center justify-center bg-pink-50 rounded-full mx-auto mb-6">
              <i className="ri-briefcase-line text-pink-400 text-4xl"></i>
            </div>
            <h1 className={`text-3xl font-extrabold mb-3 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
              Careers
            </h1>
            {isAdmin ? (
              <AdminEditField value={data.comingSoonText} onSave={(v) => set("comingSoonText", v)} multiline className="mb-6" inputClassName="text-lg text-center">
                <p className={`text-lg mb-6 ${subColor}`}>{data.comingSoonText}</p>
              </AdminEditField>
            ) : (
              <p className={`text-lg mb-6 ${subColor}`}>{data.comingSoonText}</p>
            )}
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </button>
          </div>
        </div>
        {/* Admin toggle at bottom of coming-soon page too */}
        {isAdmin && (
          <div className="w-full border-t border-gray-700 bg-gray-900 text-gray-300 px-6 py-5">
            <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold text-sm flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-400"></i>
                  Admin — Page Visibility
                </p>
                <p className="text-gray-500 text-xs mt-0.5">Toggle whether this page is live or shows "Coming Soon" to visitors</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-gray-400 text-sm">Page visible to public:</span>
                <button
                  type="button"
                  onClick={() => set("pageVisible", !data.pageVisible)}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${data.pageVisible ? "bg-green-500" : "bg-gray-600"}`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${data.pageVisible ? "translate-x-7" : "translate-x-1"}`}></span>
                </button>
                <span className={`font-bold text-sm ${data.pageVisible ? "text-green-400" : "text-gray-500"}`}>
                  {data.pageVisible ? "Live" : "Coming Soon"}
                </span>
              </label>
            </div>
          </div>
        )}
        <Footer />
        <AdminSaveBar />
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEOHead
        title="Careers at Makers Alley Co — Join Our Team in Medford, Oregon"
        description="Join the Makers Alley Co team in Medford, Oregon. We're hiring Sales Associates, Assistant Store Managers, and Store Managers who love handmade art and local community."
        keywords="Makers Alley jobs, retail jobs Medford Oregon, art shop careers, sales associate Medford, store manager Oregon, handmade retail jobs"
        canonicalPath="/careers"
        ogType="website"
        ogTitle="Careers at Makers Alley Co — Join Our Team"
        ogDescription="We're hiring in Medford, Oregon. Sales Associates, Assistant Store Managers, and Store Managers who love handmade art and local community."
        lastModified="2026-05-10"
        author="Makers Alley Co"
      />
      <PreviewModeBar />
      <Navbar />

      {isAdmin && !data.pageVisible && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-center mt-14">
          <p className="text-yellow-700 text-sm font-semibold">
            <i className="ri-eye-off-line mr-1"></i>
            This page is currently hidden from the public (showing "Coming Soon"). Use the toggle at the bottom of this page to make it visible.
          </p>
        </div>
      )}

      {/* Hero */}
      <div
        className={`pt-32 pb-16 px-6 text-center ${isDark ? "bg-gray-800" : ""}`}
        style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
      >
        <div className="max-w-3xl mx-auto">
          {isAdmin ? (
            <AdminEditField value={data.subheading} onSave={(v) => set("subheading", v)} className="mb-2" inputClassName="text-green-600 text-sm font-bold uppercase tracking-widest text-center">
              <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{data.subheading}</span>
            </AdminEditField>
          ) : (
            <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{data.subheading}</span>
          )}
          {isAdmin ? (
            <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} className="mt-3 mb-4" inputClassName="text-5xl font-extrabold text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{data.heading}</h1>
            </AdminEditField>
          ) : (
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{data.heading}</h1>
          )}
          {isAdmin ? (
            <AdminEditField value={data.intro} onSave={(v) => set("intro", v)} multiline inputClassName="text-lg text-center">
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{data.intro}</p>
            </AdminEditField>
          ) : (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{data.intro}</p>
          )}
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-16">
        {/* Job listings */}
        <div className="mb-16">
          {isAdmin ? (
            <h2 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
              Open Positions
              <span className="ml-3 text-xs font-normal text-gray-400">(toggle each position on/off below)</span>
            </h2>
          ) : (
            <h2 className={`text-2xl font-extrabold mb-8 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
              Open Positions
            </h2>
          )}

          {displayedJobs.length === 0 && (
            <div className={`rounded-2xl border p-10 text-center ${cardBg}`}>
              {isAdmin ? (
                <AdminEditField value={data.comingSoonText} onSave={(v) => set("comingSoonText", v)} multiline inputClassName="text-center text-gray-500">
                  <p className={subColor}>{data.comingSoonText}</p>
                </AdminEditField>
              ) : (
                <p className={subColor}>{data.comingSoonText}</p>
              )}
            </div>
          )}

          <div className="space-y-4 mt-6">
            {displayedJobs.map((job) => (
              <div
                key={job.id}
                className={`rounded-2xl border transition-all ${cardBg} ${!job.visible && isAdmin ? "opacity-60 border-dashed" : ""}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        {isAdmin ? (
                          <AdminEditField value={job.title} onSave={(v) => updateJob(job.id, "title", v)} inputClassName="font-bold text-xl">
                            <h3 className={`font-bold text-xl ${headingColor}`}>{job.title}</h3>
                          </AdminEditField>
                        ) : (
                          <h3 className={`font-bold text-xl ${headingColor}`}>{job.title}</h3>
                        )}
                        {isAdmin ? (
                          <AdminEditField value={job.type} onSave={(v) => updateJob(job.id, "type", v)} inputClassName="text-xs font-bold">
                            <span className="bg-pink-100 text-pink-600 text-xs font-bold px-3 py-1 rounded-full">{job.type}</span>
                          </AdminEditField>
                        ) : (
                          <span className="bg-pink-100 text-pink-600 text-xs font-bold px-3 py-1 rounded-full">{job.type}</span>
                        )}
                        {/* Admin visibility toggle per job */}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => toggleJobVisible(job.id)}
                            className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                              job.visible
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                            title={job.visible ? "Click to hide this position" : "Click to show this position"}
                          >
                            <i className={job.visible ? "ri-eye-line" : "ri-eye-off-line"}></i>
                            {job.visible ? "Visible" : "Hidden"}
                          </button>
                        )}
                      </div>
                      {isAdmin ? (
                        <AdminEditField value={job.description} onSave={(v) => updateJob(job.id, "description", v)} multiline richText inputClassName="text-sm leading-relaxed">
                          <p className={`text-sm leading-relaxed ${subColor}`}>{job.description}</p>
                        </AdminEditField>
                      ) : (
                        <p className={`text-sm leading-relaxed ${subColor}`}>{job.description}</p>
                      )}
                    </div>
                    {job.visible && (
                      <button
                        type="button"
                        onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                        className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-5 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
                      >
                        {selectedJob === job.id ? "Close" : "Apply"}
                      </button>
                    )}
                  </div>

                  {/* Requirements */}
                  <div className="mt-4">
                    <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${subColor}`}>What We're Looking For</p>
                    <ul className="space-y-1">
                      {job.requirements.map((req, ri) => (
                        <li key={ri} className={`flex items-start gap-2 text-sm ${subColor}`}>
                          <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                          {isAdmin ? (
                            <div className="flex items-center gap-1 flex-1">
                              <AdminEditField
                                value={req}
                                onSave={(v) => {
                                  const reqs = job.requirements.map((r, idx) => idx === ri ? v : r);
                                  updateJob(job.id, "requirements", reqs);
                                }}
                                inputClassName="text-sm"
                                className="flex-1"
                              >
                                <span>{req}</span>
                              </AdminEditField>
                              <button
                                onClick={() => removeRequirement(job.id, ri)}
                                className="w-5 h-5 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer flex-shrink-0"
                                title="Remove requirement"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            </div>
                          ) : (
                            <span>{req}</span>
                          )}
                        </li>
                      ))}
                      {isAdmin && (
                        <li>
                          <button
                            onClick={() => addRequirement(job.id)}
                            className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1"
                          >
                            <i className="ri-add-line"></i> Add requirement
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Inline application form */}
                {selectedJob === job.id && job.visible && (
                  <div className={`border-t px-6 py-6 ${isDark ? "border-gray-700 bg-gray-900/50" : "border-pink-100 bg-pink-50/50"}`}>
                    {status === "success" ? (
                      <div className="text-center py-6">
                        <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                          <i className="ri-check-line text-green-500 text-2xl"></i>
                        </div>
                        <h3 className={`font-bold text-lg mb-1 ${headingColor}`}>Application Submitted!</h3>
                        <p className={`text-sm ${subColor}`}>Thank you for applying! We'll review your application and be in touch soon.</p>
                        <button
                          onClick={() => { setStatus("idle"); setSelectedJob(null); }}
                          className="mt-4 bg-pink-500 hover:bg-pink-600 text-white font-bold px-5 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap"
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} id={`careers-form-${job.id}`} className="space-y-4">
                        {isAdmin ? (
                          <AdminEditField value={data.formHeading} onSave={(v) => set("formHeading", v)} className="mb-1" inputClassName="font-bold text-lg">
                            <h3 className={`font-bold text-lg ${headingColor}`}>{data.formHeading} — {job.title}</h3>
                          </AdminEditField>
                        ) : (
                          <h3 className={`font-bold text-lg ${headingColor}`}>{data.formHeading} — {job.title}</h3>
                        )}
                        {isAdmin ? (
                          <AdminEditField value={data.formSubheading} onSave={(v) => set("formSubheading", v)} multiline inputClassName="text-sm">
                            <p className={`text-sm ${subColor}`}>{data.formSubheading}</p>
                          </AdminEditField>
                        ) : (
                          <p className={`text-sm ${subColor}`}>{data.formSubheading}</p>
                        )}

                        <input type="hidden" name="position" value={job.title} />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Full Name *</label>
                            <input type="text" name="fullName" required placeholder="Jane Smith" className={inputCls} />
                          </div>
                          <div>
                            <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Email Address *</label>
                            <input type="email" name="email" required placeholder="jane@example.com" className={inputCls} />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Phone Number</label>
                            <input type="tel" name="phone" placeholder="(555) 000-0000" className={inputCls} />
                          </div>
                          <div>
                            <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Availability</label>
                            <select name="availability" className={`${inputCls} cursor-pointer`}>
                              <option value="">Select availability</option>
                              <option value="Full-Time">Full-Time</option>
                              <option value="Part-Time">Part-Time</option>
                              <option value="Weekends Only">Weekends Only</option>
                              <option value="Flexible">Flexible</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Why do you want to work at Makers Alley Co.? *
                          </label>
                          <textarea
                            name="whyApply"
                            required
                            rows={4}
                            maxLength={500}
                            placeholder="Tell us a bit about yourself and why you'd be a great fit..."
                            className={`${inputCls} resize-none`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-semibold mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Upload Resume *
                          </label>
                          <div className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-colors ${
                            resumeFile || resumeUrl
                              ? "border-green-300 bg-green-50"
                              : isDark ? "border-gray-600 hover:border-pink-400" : "border-pink-200 hover:border-pink-400"
                          }`}>
                            <input
                              type="file"
                              name="resume"
                              accept=".pdf,.doc,.docx"
                              className="hidden"
                              id={`resume-upload-${job.id}`}
                              onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                setResumeFile(file);
                                setResumeUrl(null);
                                setResumeError(null);
                              }}
                            />
                            <label htmlFor={`resume-upload-${job.id}`} className="cursor-pointer">
                              {resumeUploading ? (
                                <div className="flex items-center justify-center gap-2">
                                  <i className="ri-loader-4-line animate-spin text-pink-400 text-xl"></i>
                                  <span className="text-pink-600 font-semibold text-sm">Uploading...</span>
                                </div>
                              ) : resumeFile ? (
                                <div className="flex items-center justify-center gap-2">
                                  <i className="ri-file-text-line text-green-500 text-xl"></i>
                                  <span className="text-green-600 font-semibold text-sm">{resumeFile.name}</span>
                                  <span className="text-gray-400 text-xs">({(resumeFile.size / 1024).toFixed(0)} KB)</span>
                                </div>
                              ) : resumeUrl ? (
                                <div className="flex items-center justify-center gap-2">
                                  <i className="ri-file-text-line text-green-500 text-xl"></i>
                                  <span className="text-green-600 font-semibold text-sm">Resume uploaded</span>
                                  <a
                                    href={resumeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs text-pink-500 underline hover:text-pink-700 cursor-pointer"
                                  >
                                    View
                                  </a>
                                </div>
                              ) : (
                                <>
                                  <i className={`ri-upload-cloud-line text-2xl mb-1 ${isDark ? "text-gray-500" : "text-pink-300"}`}></i>
                                  <p className={`text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>Click to upload your resume</p>
                                  <p className={`text-xs mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}>PDF, DOC, or DOCX</p>
                                </>
                              )}
                            </label>
                          </div>
                        </div>
                        {resumeError && <p className="text-red-500 text-sm">{resumeError}</p>}

                        {status === "error" && <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>}

                        <div className="flex gap-3">
                          <button
                            type="submit"
                            disabled={status === "submitting" || resumeUploading}
                            className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {status === "submitting" ? (
                              <><i className="ri-loader-4-line animate-spin"></i>Submitting...</>
                            ) : (
                              <><i className="ri-send-plane-line"></i>Submit Application</>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedJob(null)}
                            className={`px-5 py-3 rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap border transition-colors ${isDark ? "border-gray-600 text-gray-400 hover:border-gray-400" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Admin toggle — at the BOTTOM of the page, always accessible */}
      {isAdmin && (
        <div className="w-full border-t border-gray-700 bg-gray-900 text-gray-300 px-6 py-5">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold text-sm flex items-center gap-2">
                <i className="ri-shield-check-line text-green-400"></i>
                Admin — Page Visibility
              </p>
              <p className="text-gray-500 text-xs mt-0.5">Toggle whether this page is live or shows "Coming Soon" to visitors</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <span className="text-gray-400 text-sm">Page visible to public:</span>
              <button
                type="button"
                onClick={() => set("pageVisible", !data.pageVisible)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${data.pageVisible ? "bg-green-500" : "bg-gray-600"}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${data.pageVisible ? "translate-x-7" : "translate-x-1"}`}></span>
              </button>
              <span className={`font-bold text-sm ${data.pageVisible ? "text-green-400" : "text-gray-500"}`}>
                {data.pageVisible ? "Live" : "Coming Soon"}
              </span>
            </label>
          </div>
        </div>
      )}

      <Footer />
      <AdminSaveBar />
    </div>
  );
}
